import type { UserRole } from '@familychef/shared';
import { ArrowLeft, Shield, ShieldCheck, User } from 'lucide-react';

import { useUserDetail, useUpdateUserRole } from '../../hooks/useUsers';

import { AppShell } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { useNavigate, useParams } from '@/lib/router';

const ROLE_CONFIG: Record<UserRole, { label: string; color: string; icon: typeof User }> = {
  user: { label: 'Utilisateur', color: 'bg-slate-100 text-slate-700', icon: User },
  moderator: { label: 'Modérateur', color: 'bg-blue-100 text-blue-700', icon: Shield },
  admin: { label: 'Admin', color: 'bg-orange-100 text-orange-700', icon: ShieldCheck },
};

const ROLES: UserRole[] = ['user', 'moderator', 'admin'];

export function UserDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { data: user, isLoading } = useUserDetail(id);
  const updateRole = useUpdateUserRole(id);

  if (isLoading) {
    return (
      <AppShell>
        <TopBar title="Chargement..." />
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
        </div>
      </AppShell>
    );
  }

  if (!user) return null;

  const currentConfig = ROLE_CONFIG[user.role];

  return (
    <AppShell>
      <TopBar title={user.name ?? user.email} />
      <div className="mx-auto max-w-3xl p-6">
        <button
          onClick={() => void navigate({ to: '/users' })}
          className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={15} /> Retour aux utilisateurs
        </button>

        {/* Profil */}
        <div className="card mb-5">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-xl font-bold text-green-700">
              {(user.name ?? user.email).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-900">
                {user.name ?? '(sans nom)'}
              </h3>
              <p className="text-sm text-slate-500">{user.email}</p>
              <p className="text-xs text-slate-400">
                Inscrit le{' '}
                {new Date(user.created_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${currentConfig.color}`}
            >
              <currentConfig.icon size={14} />
              {currentConfig.label}
            </span>
          </div>
        </div>

        {/* Rôle */}
        <div className="card mb-5">
          <h3 className="mb-3 font-semibold text-slate-900">Rôle global</h3>
          <div className="flex flex-wrap gap-2">
            {ROLES.map((role) => {
              const config = ROLE_CONFIG[role];
              const Icon = config.icon;
              const isActive = user.role === role;
              return (
                <button
                  key={role}
                  onClick={() => {
                    if (!isActive) updateRole.mutate(role);
                  }}
                  disabled={updateRole.isPending}
                  className={`flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
                    isActive
                      ? `${config.color} border-current`
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={16} />
                  {config.label}
                </button>
              );
            })}
          </div>
          {updateRole.isError && (
            <p className="mt-2 text-sm text-red-600">
              Erreur lors du changement de rôle. Vérifiez vos permissions.
            </p>
          )}
        </div>

        {/* Famille */}
        <div className="card mb-5">
          <h3 className="mb-3 font-semibold text-slate-900">Famille</h3>
          {user.family ? (
            <div className="flex items-center gap-2">
              <span className="badge bg-green-100 text-green-700">{user.family.name}</span>
              <button
                onClick={() => void navigate({ to: `/families/${user.family!.id}` })}
                className="text-sm text-green-600 hover:text-green-700"
              >
                Voir la famille →
              </button>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Aucune famille active</p>
          )}
        </div>

        {/* Recettes récentes */}
        <div className="card">
          <h3 className="mb-3 font-semibold text-slate-900">10 dernières recettes créées</h3>
          {user.recipes.length === 0 ? (
            <p className="text-sm text-slate-400">Aucune recette</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {user.recipes.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-slate-900">{r.title}</span>
                  <span className="text-xs text-slate-400">
                    {new Date(r.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
